import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// "Request a date": the speaking inquiry form, as a dialog over the whole
// page. Seven fields (name, email, organization, event type, preferred date,
// city, details). Timeline, audience size and budget came out in Oct 2026 at
// Max's request: fewer questions, and the details box covers the rest.
//
// Rendered through a portal to <body>: the sections it used to sit inside
// carry scroll-driven transforms, which turn `position: fixed` into
// "fixed to that section" (the dialog opened mid-page, under the nav).

const EVENT_TYPES = [
  { id: "corporate", label: "Corporate / keynote" },
  { id: "fundraiser", label: "Fundraiser / charity" },
  { id: "private", label: "Private dinner / Q&A" },
  { id: "media", label: "Media / interview" },
  { id: "school", label: "School / clinic" },
  { id: "other", label: "Something else" },
];

async function submitRequest(payload) {
  const res = await fetch("/api/speaking-request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.error || `HTTP ${res.status}` };
  }
  return res.json();
}

const EMPTY = {
  name: "",
  email: "",
  organization: "",
  eventType: "corporate",
  eventDate: "",
  city: "",
  details: "",
};

export default function RequestDateModal({ open, onClose }) {
  const [step, setStep] = useState("form");
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [referenceId, setReferenceId] = useState("");
  const [form, setForm] = useState(EMPTY);

  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);
  const fieldRefs = {
    name: useRef(null),
    email: useRef(null),
    details: useRef(null),
  };

  useEffect(() => {
    if (open) {
      setStep("form");
      setErrorMsg("");
      setFieldErrors({});
      setReferenceId("");
      setTimeout(() => firstInputRef.current?.focus({ preventScroll: true }), 120);
    }
  }, [open]);

  // While it's open the page under it doesn't scroll, and Escape closes it.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const setField = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setFieldErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const scrollToFirstError = (errs) => {
    const order = ["name", "email", "details"];
    const first = order.find((k) => errs[k]);
    if (!first) return;
    const el = fieldRefs[first]?.current;
    if (!el) return;
    // The dialog is its own scroll container, so scroll within the dialog.
    const dialog = dialogRef.current;
    if (dialog) {
      const dialogRect = dialog.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      dialog.scrollTo({
        top: dialog.scrollTop + (elRect.top - dialogRect.top) - 24,
        behavior: "smooth",
      });
    }
    const focusable = el.querySelector("input, textarea");
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 350);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.trim() || !form.email.includes("@")) errs.email = "Valid email required";
    if (!form.details.trim()) errs.details = "Tell us a bit about the event";
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setErrorMsg("");
      scrollToFirstError(errs);
      return;
    }
    setFieldErrors({});
    setStep("sending");
    setErrorMsg("");
    try {
      const res = await submitRequest({ ...form, submittedAt: new Date().toISOString() });
      if (res.ok) {
        setReferenceId(res.id || "");
        setStep("success");
      } else {
        setErrorMsg(res.error || "Something went wrong. Please try again.");
        setStep("error");
      }
    } catch (err) {
      setErrorMsg(err?.message || "Network error. Please try again.");
      setStep("error");
    }
  };

  const dialog = (
    <div
      className="rdm-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rdm-title"
    >
      <div className="rdm-dialog" ref={dialogRef}>
        <button type="button" className="rdm-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {step === "success" ? (
          <div className="rdm-success">
            <div className="rdm-success-icon" aria-hidden="true">
              ✓
            </div>
            <h3 className="rdm-success-h">Request received.</h3>
            <p className="rdm-success-p">
              Thanks, {form.name.split(" ")[0] || "friend"}. Greg or someone from his team will
              reach out within a couple of business days.
            </p>
            {referenceId && (
              <div className="rdm-ref mono">Reference · {referenceId.toUpperCase()}</div>
            )}
            <button type="button" className="rdm-submit" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rdm-form" noValidate>
            <header className="rdm-head">
              <span className="mono rdm-eyebrow">Book Greg</span>
              <h3 id="rdm-title" className="rdm-title">
                Tell us about <em>your event.</em>
              </h3>
              <p className="rdm-sub">
                A few details and we'll get back to you with dates and pricing, usually within two
                business days.
              </p>
            </header>

            <div className="rdm-grid">
              <label
                ref={fieldRefs.name}
                className={`rdm-field ${fieldErrors.name ? "has-err" : ""}`}
              >
                <span className="rdm-label">
                  Your name <span className="rdm-req">*</span>
                </span>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={form.name}
                  onChange={setField("name")}
                  required
                  autoComplete="name"
                />
                {fieldErrors.name && <span className="rdm-field-err">{fieldErrors.name}</span>}
              </label>

              <label
                ref={fieldRefs.email}
                className={`rdm-field ${fieldErrors.email ? "has-err" : ""}`}
              >
                <span className="rdm-label">
                  Email <span className="rdm-req">*</span>
                </span>
                <input
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  required
                  autoComplete="email"
                  inputMode="email"
                />
                {fieldErrors.email && <span className="rdm-field-err">{fieldErrors.email}</span>}
              </label>

              <label className="rdm-field rdm-field-wide">
                <span className="rdm-label">Organization</span>
                <input
                  type="text"
                  value={form.organization}
                  onChange={setField("organization")}
                  autoComplete="organization"
                  placeholder="Company, school, club or charity"
                />
              </label>

              <div className="rdm-field rdm-field-wide">
                <span className="rdm-label">What kind of event</span>
                <div className="rdm-pillrow">
                  {EVENT_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      className={`rdm-pill ${form.eventType === t.id ? "is-active" : ""}`}
                      aria-pressed={form.eventType === t.id}
                      onClick={() => setForm((f) => ({ ...f, eventType: t.id }))}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="rdm-field">
                <span className="rdm-label">Preferred date</span>
                <input type="date" value={form.eventDate} onChange={setField("eventDate")} />
                <span className="rdm-hint">Leave it blank if you're flexible.</span>
              </label>

              <label className="rdm-field">
                <span className="rdm-label">City, State</span>
                <input
                  type="text"
                  value={form.city}
                  onChange={setField("city")}
                  placeholder="Kansas City, MO"
                  autoComplete="address-level2"
                />
              </label>

              <label
                ref={fieldRefs.details}
                className={`rdm-field rdm-field-wide ${fieldErrors.details ? "has-err" : ""}`}
              >
                <span className="rdm-label">
                  About the event <span className="rdm-req">*</span>
                </span>
                <textarea
                  rows={4}
                  value={form.details}
                  onChange={setField("details")}
                  required
                  placeholder="The occasion, how many people, what you'd like Greg to talk about, and anything else (a signing, a Q&A, a budget in mind)."
                />
                {fieldErrors.details && (
                  <span className="rdm-field-err">{fieldErrors.details}</span>
                )}
              </label>
            </div>

            {errorMsg && <div className="rdm-error">{errorMsg}</div>}

            <footer className="rdm-foot">
              <button type="button" className="rdm-cancel" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="rdm-submit" disabled={step === "sending"}>
                {step === "sending" ? "Sending…" : "Send the request"}
                {step !== "sending" && (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M5 12h13M13 6l6 6-6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
  return createPortal(dialog, document.body);
}

const rdmStyle = document.createElement("style");
rdmStyle.textContent = `
.rdm-overlay {
  position: fixed; inset: 0; z-index: 1000;
  display: grid; place-items: center;
  padding: 24px;
  background: rgba(2, 6, 14, 0.72);
  backdrop-filter: blur(10px) saturate(120%);
  -webkit-backdrop-filter: blur(10px) saturate(120%);
  overflow-y: auto;
  animation: rdm-fade-in 200ms ease-out;
}
@keyframes rdm-fade-in { from { opacity: 0; } }
.rdm-dialog {
  position: relative;
  width: min(640px, 100%);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  overscroll-behavior: contain;
  background:
    radial-gradient(80% 50% at 50% 0%, rgba(0, 70, 135, 0.35), transparent 70%),
    linear-gradient(180deg, #0c1c31 0%, var(--navy-900) 100%);
  border-radius: 18px;
  box-shadow: inset 0 0 0 1px rgba(189, 155, 96, 0.35), 0 50px 120px -30px rgba(0, 0, 0, 0.9);
  animation: rdm-rise 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
@keyframes rdm-rise { from { opacity: 0; transform: translateY(18px) scale(0.985); } }
.rdm-close {
  position: absolute; top: 16px; right: 16px; z-index: 2;
  display: grid; place-items: center; width: 38px; height: 38px;
  border-radius: 50%; border: 1px solid var(--rule); background: rgba(5, 13, 26, 0.6);
  color: var(--bone-dim); cursor: pointer;
  transition: background 180ms, color 180ms, border-color 180ms;
}
.rdm-close svg { width: 18px; height: 18px; }
.rdm-close:hover { background: var(--navy-700); color: var(--bone); border-color: var(--gold); }

.rdm-form { padding: 40px 40px 32px; }
.rdm-head { margin-bottom: 26px; padding-right: 40px; }
.rdm-eyebrow { color: var(--gold); font-size: 0.6875rem; letter-spacing: 0.22em; text-transform: uppercase; }
.rdm-title { font-family: var(--serif); font-size: clamp(1.75rem, 4vw, 2.375rem); font-weight: 500; letter-spacing: -0.015em; color: var(--bone); margin: 10px 0 10px; line-height: 1.05; }
.rdm-title em { color: var(--royal-blue-glow); font-style: italic; }
.rdm-sub { font-size: 0.9375rem; line-height: 1.5; color: var(--bone-dim); margin: 0; max-width: 48ch; }
.rdm-req { color: var(--gold); }

.rdm-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 16px; }
.rdm-field { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.rdm-field-wide { grid-column: 1 / -1; }
.rdm-label { font-family: var(--mono); font-size: 0.625rem; letter-spacing: 0.18em; color: var(--bone-dim); text-transform: uppercase; }
.rdm-hint { font-size: 0.75rem; color: rgba(232, 227, 214, 0.55); }
.rdm-field input, .rdm-field textarea {
  font-family: inherit; font-size: 0.9375rem;
  background: rgba(5, 13, 26, 0.6); color: var(--bone);
  border: 1px solid rgba(189, 155, 96, 0.3); border-radius: 10px;
  padding: 12px 14px; min-height: 46px; outline: none;
  transition: border-color 180ms, box-shadow 180ms;
  color-scheme: dark;
}
.rdm-field input::placeholder, .rdm-field textarea::placeholder { color: rgba(232, 227, 214, 0.4); }
.rdm-field input:focus, .rdm-field textarea:focus { border-color: var(--royal-blue-glow); box-shadow: 0 0 0 3px rgba(43, 138, 255, 0.18); }
.rdm-field textarea { resize: vertical; min-height: 110px; line-height: 1.5; }

.rdm-pillrow { display: flex; flex-wrap: wrap; gap: 8px; }
.rdm-pill {
  font-family: inherit; font-size: 0.8125rem;
  background: rgba(5, 13, 26, 0.6); color: var(--bone-dim);
  border: 1px solid rgba(189, 155, 96, 0.3); border-radius: 999px;
  padding: 10px 15px; min-height: 42px;
  cursor: pointer;
  transition: background 160ms, border-color 160ms, color 160ms;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  display: inline-flex; align-items: center;
  line-height: 1;
}
@media (hover: hover) {
  .rdm-pill:hover { border-color: var(--bone-dim); color: var(--bone); }
}
.rdm-pill.is-active { background: var(--gold); color: var(--navy-900); border-color: var(--gold); font-weight: 600; }

.rdm-field.has-err input,
.rdm-field.has-err textarea { border-color: #ff6b5e; box-shadow: 0 0 0 3px rgba(255, 74, 61, 0.14); }
.rdm-field-err { font-family: var(--mono); font-size: 0.625rem; letter-spacing: 0.16em; color: #ff8a80; text-transform: uppercase; }

.rdm-error { margin-top: 18px; padding: 12px 14px; border: 1px solid rgba(255, 74, 61, 0.4); background: rgba(255, 74, 61, 0.08); color: #ffb8b8; font-size: 0.875rem; border-radius: 10px; }

.rdm-foot { margin-top: 26px; display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.rdm-cancel { font-family: inherit; font-size: 0.875rem; background: transparent; color: var(--bone-dim); border: 1px solid var(--rule-strong); padding: 12px 20px; border-radius: 999px; cursor: pointer; transition: color 180ms, border-color 180ms; }
.rdm-cancel:hover { color: var(--bone); border-color: var(--gold); }
.rdm-submit { display: inline-flex; align-items: center; gap: 10px; font-family: inherit; font-size: 0.9375rem; font-weight: 600; background: var(--gold); color: var(--navy-900); border: 1px solid var(--gold); padding: 12px 22px; border-radius: 999px; cursor: pointer; transition: background 180ms, transform 180ms; }
.rdm-submit svg { width: 18px; height: 18px; }
.rdm-submit:hover:not(:disabled) { background: var(--gold-bright); transform: translateY(-1px); }
.rdm-submit:disabled { opacity: 0.6; cursor: progress; }

.rdm-success { padding: 56px 40px 44px; text-align: center; }
.rdm-success-icon { width: 64px; height: 64px; margin: 0 auto 24px; border-radius: 50%; background: rgba(189, 155, 96, 0.12); border: 1px solid var(--gold); color: var(--gold-bright); font-size: 2rem; display: grid; place-items: center; }
.rdm-success-h { font-family: var(--serif); font-size: 2rem; color: var(--bone); margin: 0 0 12px; font-weight: 500; letter-spacing: -0.015em; }
.rdm-success-p { color: var(--bone-dim); font-size: 0.9375rem; line-height: 1.55; max-width: 42ch; margin: 0 auto 24px; }
.rdm-ref { font-size: 0.625rem; letter-spacing: 0.22em; text-transform: uppercase; color: var(--gold); margin-bottom: 28px; }

@media (max-width: 640px) {
  .rdm-overlay { padding: 10px; align-items: flex-start; }
  .rdm-dialog { max-height: calc(100vh - 20px); max-height: calc(100dvh - 20px); border-radius: 16px; }
  .rdm-form { padding: 28px 20px 22px; }
  .rdm-head { padding-right: 44px; }
  .rdm-success { padding: 44px 22px 32px; }
  .rdm-grid { grid-template-columns: 1fr; gap: 14px; }
  .rdm-title { font-size: 1.625rem; }
  .rdm-foot { flex-direction: column-reverse; align-items: stretch; }
  .rdm-submit { justify-content: center; }
}
@media (prefers-reduced-motion: reduce) {
  .rdm-overlay, .rdm-dialog { animation: none; }
}
`;
document.head.appendChild(rdmStyle);
